import { applicationPortalDialog as fdy } from "./foundry-parts.generated";
/**
 * @fileoverview AppPortalResultList — one category's AppCards under a heading
 * (e.g. "Application development").
 *
 * THIS ELEMENT IS THE LIST. It had no Foundry class and was itself wrapped in an
 * unclassed `div` that only existed to hold a scroll-anchor ref, so the section
 * was two levels deeper than Foundry's and nothing below it lined up. The ref
 * comes in as a prop instead; a wrapper element whose whole job is to be a ref
 * target is a wrapper that does not need to exist.
 *
 * `heading@1kxq4k1` and `divider@1kxq4k1` name the module explicitly because the
 * namespace ships two near-identical ones — `1kxq4k1` styles the nine category
 * sections, `12czwt` a separate trailing section — and the part names collide.
 */

import { Divider } from "@blueprintjs/core";
import type { ReactNode, Ref } from "react";

interface AppPortalResultListProps {
  title: string;
  /** The AppCard rows. */
  children: ReactNode;
  /** Stable id for `aria-labelledby` references. */
  headingId?: string;
  /** Scroll-anchor target, so the rail can bring this section into view. */
  sectionRef?: Ref<HTMLDivElement>;
  /** Render a Blueprint Divider after the list (for separating multiple lists). */
  withTrailingDivider?: boolean;
  className?: string;
}

export function AppPortalResultList({
  title,
  children,
  headingId,
  sectionRef,
  withTrailingDivider = false,
  className,
}: AppPortalResultListProps) {
  return (
    <div
      data-testid="app-portal-result-list"
      ref={sectionRef}
      className={[fdy["list@1kxq4k1"], "bp-app-portal-result-list", className]
        .filter(Boolean)
        .join(" ")}
    >
      <h5
        id={headingId}
        className={`bp6-heading bp6-text-muted ${fdy["heading@1kxq4k1"]} bp-app-portal-result-list-heading`}
      >
        {title}
      </h5>
      {children}
      {withTrailingDivider && (
        <Divider className={`${fdy["divider@1kxq4k1"]} bp-app-portal-result-list-divider`} />
      )}
    </div>
  );
}
